/**
 * BatchReplayStatus - Shows batch replay progress and statistics
 */
import React from 'react';
import { RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';

const BatchReplayStatus = ({ batchProgress }) => {
  if (!batchProgress || !batchProgress?.isActive) {
    return null;
  }

  const { currentBatch, totalBatches, currentType, processedOps, totalOps, stats } = batchProgress;
  const progress = totalOps > 0 ? (processedOps / totalOps) * 100 : 0;

  return (
    <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
      <div className="bg-card border border-border rounded-lg shadow-xl p-4 min-w-[320px]">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <RefreshCw className="w-5 h-5 text-primary animate-spin" />
          <h3 className="text-sm font-semibold text-foreground">
            Syncing Operations
          </h3>
        </div>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Batch {currentBatch} of {totalBatches}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary h-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Current Operation Type */}
        <div className="flex items-center justify-between text-xs mb-3">
          <span className="text-muted-foreground">Current:</span>
          <span className="font-medium text-foreground capitalize">
            {currentType || 'Processing...'}
          </span>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-muted rounded p-2">
              <div className="text-muted-foreground mb-1">Operations</div>
              <div className="font-semibold text-foreground">
                {processedOps} / {totalOps}
              </div>
            </div>
            <div className="bg-muted rounded p-2">
              <div className="text-muted-foreground mb-1">Optimized</div>
              <div className="font-semibold text-success">
                {stats?.savedOperations > 0 ? `-${stats?.savedOperations}` : '0'}
              </div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {batchProgress?.lastResult && (
          <div className="mt-3 flex items-start gap-2 text-xs">
            {batchProgress?.lastResult?.success ? (
              <CheckCircle className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
            )}
            <span className={batchProgress?.lastResult?.success ? 'text-success' : 'text-destructive'}>
              {batchProgress?.lastResult?.message}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchReplayStatus;
